"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { RUNS_TEST_CALCULATOR_STRINGS } from "@/components/tools/runs-test-calculator";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import type { RunsResult } from "@/lib/tool-kit/stats-tests-more/runs-test";

/** Results for the runs test calculator. */

type Strings = typeof RUNS_TEST_CALCULATOR_STRINGS;

const DRAWN_LIMIT = 200;

/** The sequence split into its runs, one outlined block per run. */
function RunStrip({
  r,
  labels,
}: {
  r: RunsResult;
  labels: readonly [string, string];
}) {
  const blocks: { key: string; text: string }[] = [];
  let offset = 0;
  for (const [index, length] of r.runLengths.entries()) {
    if (offset >= DRAWN_LIMIT) {
      break;
    }
    const symbol = r.symbols[offset] === 1 ? labels[0] : labels[1];
    const shown = Math.min(length, DRAWN_LIMIT - offset);
    blocks.push({ key: `run-${index + 1}`, text: symbol.repeat(shown) });
    offset += length;
  }
  return (
    <div className="flex flex-wrap gap-2 font-mono text-sm">
      {blocks.map((block) => (
        <span
          className="break-all rounded-md border border-border bg-card px-2 py-1 text-foreground"
          key={block.key}
        >
          {block.text}
        </span>
      ))}
    </div>
  );
}

export function RunsTestResults({
  r,
  labels,
  cutoff,
  dropped,
  alpha,
  continuity,
  locale,
  strings,
  fmt,
}: {
  r: RunsResult;
  labels: readonly [string, string];
  cutoff: number | null;
  dropped: number;
  alpha: number;
  continuity: boolean;
  locale: string;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const reject = r.p < alpha;
  const sigma = Math.sqrt(r.variance);
  const apa =
    r.method === "exact"
      ? richTemplate(strings.apaExactTemplate, {
          r: fmt(r.runs),
          n1: fmt(r.n1),
          n2: fmt(r.n2),
          p: apaP(r.p, fmt),
        })
      : richTemplate(strings.apaNormalTemplate, {
          r: fmt(r.runs),
          n1: fmt(r.n1),
          n2: fmt(r.n2),
          z: fixed(r.z, 2, fmt),
          p: apaP(r.p, fmt),
        });
  const otherP = r.method === "exact" ? r.pNormal : r.pExact;
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.runs} value={fmt(r.runs)} />
        <StatCard label={strings.n1} value={fmt(r.n1)} />
        <StatCard label={strings.n2} value={fmt(r.n2)} />
        <StatCard label={strings.expected} value={n(r.mean)} />
        <StatCard label={strings.z} value={n(r.z)} />
        <StatCard emphasis label={strings.pValue} value={formatP(r.p, fmt)} />
      </StatGrid>
      <Decision reject={reject}>
        {t(reject ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(r.p, fmt),
        })}
      </Decision>
      <p className={HINT}>
        {t(r.method === "exact" ? strings.methodExact : strings.methodNormal, {
          p: formatP(otherP, fmt),
        })}
      </p>
      {cutoff !== null ? (
        <p className="text-foreground text-sm">
          {t(strings.cutoffIs, {
            cutoff: n(cutoff),
            high: fmt(r.n1),
            low: fmt(r.n2),
          })}
          {dropped > 0
            ? ` ${t(tPlural(strings.dropped, locale, dropped), { count: fmt(dropped) })}`
            : ""}
        </p>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.sequenceHeading}</h3>
      <p className={HINT}>
        {strings.sequenceHint}
        {r.n > DRAWN_LIMIT ? ` ${strings.sequenceCut}` : ""}
      </p>
      <RunStrip labels={labels} r={r} />
      <h3 className={WIDGET_H3}>{strings.allPHeading}</h3>
      <ResultTable
        head={[strings.alternative, strings.pExact, strings.pNormal]}
        rows={[
          {
            key: "two",
            cells: [
              strings.twoSided,
              formatP(
                Math.min(1, 2 * Math.min(r.pExactLess, r.pExactGreater)),
                fmt
              ),
              r.alternative === "two-sided" ? formatP(r.pNormal, fmt) : "",
            ],
          },
          {
            key: "less",
            cells: [strings.less, formatP(r.pExactLess, fmt), ""],
          },
          {
            key: "greater",
            cells: [strings.greater, formatP(r.pExactGreater, fmt), ""],
          },
        ]}
      />
      <Working heading={strings.howCalculated}>
        <p>
          {t(strings.workingMean, {
            n1: fmt(r.n1),
            n2: fmt(r.n2),
            n: fmt(r.n),
            mu: n(r.mean),
          })}
        </p>
        <p>
          {t(strings.workingVariance, {
            variance: n(r.variance),
            sigma: n(sigma),
          })}
        </p>
        <p>
          {t(continuity ? strings.workingZCc : strings.workingZ, {
            r: fmt(r.runs),
            mu: n(r.mean),
            sigma: n(sigma),
            z: n(r.z),
          })}
        </p>
        <p>
          {t(strings.workingExact, {
            r: fmt(r.runs),
            less: formatP(r.pExactLess, fmt),
            greater: formatP(r.pExactGreater, fmt),
          })}
        </p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNote}
        parts={apa}
      />
    </section>
  );
}
